import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FaArrowLeft } from "react-icons/fa";
import { API_BASE_URL } from "../../config/apiConfig";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const ScoreRing = ({ label, score }) => {
    const value = score == null ? 0 : score;
    const color = value >= 70 ? "text-green-500" : value >= 50 ? "text-yellow-500" : "text-red-500";
    return (
        <div className="bg-white rounded-xl shadow-sm p-6 text-center">
            <p className={`text-4xl font-bold ${color}`}>{score == null ? "—" : `${score}%`}</p>
            <p className="text-sm text-gray-500 mt-2">{label}</p>
        </div>
    );
};

const Readiness = () => {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const studentId = params.get("studentId");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchReadiness = async () => {
            try {
                const url = new URL(`${API_BASE_URL}/api/readiness`);
                if (studentId) url.searchParams.set("studentId", studentId);
                const res = await fetch(url, { headers: authHeaders() });
                const json = await res.json();
                if (!res.ok) throw new Error(json.message || "Failed to load readiness");
                setData(json.data);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchReadiness();
    }, [studentId]);

    return (
        <div className="min-h-screen bg-gray-50">
            <nav className="bg-[#2148C0] shadow-md px-6 py-4 flex items-center gap-4">
                <button onClick={() => navigate(-1)} className="text-white"><FaArrowLeft size={18} /></button>
                <h1 className="text-xl font-bold text-white">Exam Readiness</h1>
            </nav>

            <div className="max-w-3xl mx-auto p-6 space-y-6">
                {loading && <p className="text-gray-500">Loading...</p>}

                {!loading && data && (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <ScoreRing label="WAEC Readiness" score={data.waecScore} />
                            <ScoreRing label="JAMB Readiness" score={data.jambScore} />
                        </div>

                        <div>
                            <h3 className="font-semibold text-gray-800 mb-3">Per-subject readiness</h3>
                            {data.subjects.length === 0 && <p className="text-sm text-gray-500">No practice history yet.</p>}
                            <div className="space-y-2">
                                {data.subjects.sort((a, b) => a.score - b.score).map((s) => (
                                    <div key={s.subject} className="bg-white rounded-xl shadow-sm p-4">
                                        <div className="flex justify-between mb-2">
                                            <span className="font-medium text-gray-800">{s.subject}</span>
                                            <span className="font-bold text-gray-700">{s.score}%</span>
                                        </div>
                                        <div className="w-full bg-gray-100 rounded-full h-2">
                                            <div className={`h-2 rounded-full ${s.score >= 70 ? "bg-green-500" : s.score >= 50 ? "bg-yellow-500" : "bg-red-500"}`}
                                                style={{ width: `${s.score}%` }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button onClick={() => navigate(`/study-plan${studentId ? `?studentId=${studentId}` : ""}`)}
                            className="text-blue600 font-semibold text-sm">
                            See what to study next →
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default Readiness;
